import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, IconButton, Link, Skeleton, useTheme } from '@mui/material';
import { DESIGNBUILDER_SUPPORT_EMAIL, idfErrorDialogTextSx } from 'components/IdfParseResultStep';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getRevisionResult } from 'services/revisions';
import { getRevisionErrorHtmlContent } from 'utils/utils';

const RevisionError = ({
  revisionID,
  projectID,
  businessID,
  open,
  setShowRevisionError,
}: {
  revisionID: string;
  projectID: string;
  businessID: string | undefined;
  open: boolean;
  setShowRevisionError: any;
}) => {
  const theme = useTheme();
  const [errorEneregyPlus, setErrorEneregyPlus] = useState(false);
  const { t, i18n } = useTranslation();
  const {
    data: resultdata,
    isLoading: isresultLoading,
    isError: isresultError,
  } = useQuery(
    ['GetRevisionResultByIDERROR', revisionID, projectID, businessID],
    async () => await getRevisionResult(revisionID, projectID, businessID),
    { retry: false, enabled: open && Boolean(revisionID && projectID) }
  );

  const messages = resultdata?.data?.error || { message: [] };
  const messagesEneregyPlusError = resultdata?.data?.error?.message?.some(
    (message: string) => message.includes('Program')
  );

  const isParserError = useMemo(() => {
    if (messagesEneregyPlusError) return false;
    const list = messages?.message;
    if (!Array.isArray(list) || list.length === 0) return false;
    if (messages?.source === 'parser') return true;
    return list.every(
      (m: string) =>
        m === 'קובץ אינו תקין' ||
        (typeof m === 'string' && m.startsWith('REVISION_'))
    );
  }, [messages, messagesEneregyPlusError]);

  const isHebrewUi = Boolean(i18n.language?.startsWith('he'));
  const closeDialog = () => setShowRevisionError(false);

  useEffect(() => {
    if (messagesEneregyPlusError) {
      setErrorEneregyPlus(true);
    }
  }, [resultdata, messagesEneregyPlusError, theme.direction]);

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      dir={errorEneregyPlus ? 'ltr' : theme.direction}
      maxWidth={false}
      className="revision-error-dialog"
      sx={{
        '& .MuiDialog-paper': {
          position: 'relative',
          borderRadius: '8px',
          width: 'max-content',
          maxWidth: 'calc(100vw - 32px)',
          m: 2,
          overflow: 'visible',
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={closeDialog}
        sx={{
          position: 'absolute',
          top: 8,
          insetInlineEnd: 8,
          zIndex: 1,
          color: 'grey.500',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          px: 3,
          pt: 5,
          pb: 3,
          overflow: 'visible',
          width: 'max-content',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
            width: 'max-content',
            maxWidth: '100%',
            '& a': {
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
            },
          }}
        >
          {isresultLoading ? (
            <Skeleton variant="text" sx={{ fontSize: '1rem', minWidth: 280 }} />
          ) : (
            <Box
              sx={{
                ...idfErrorDialogTextSx,
                maxWidth: errorEneregyPlus ? 'min(720px, calc(100vw - 80px))' : '100%',
                maxHeight: errorEneregyPlus ? '50vh' : 'none',
                overflow: errorEneregyPlus ? 'auto' : 'visible',
                fontWeight: isParserError ? 700 : 400,
                fontSize: isParserError ? '18px' : '14px',
              }}
            >
              {getRevisionErrorHtmlContent(
                isresultError && !messages?.message?.length
                  ? { message: ['GENERAL_ERROR'] }
                  : messages
              )}
            </Box>
          )}

          <Box component="p" sx={{ ...idfErrorDialogTextSx, fontSize: '16px' }}>
            {t('TRAINING_VIDEOS_1')}{' '}
            <Link href="/how-it-works" target="_blank" underline="always">
              {t('TRAINING_VIDEOS_2')}
            </Link>
          </Box>

          {isParserError && (
            <Box
              component="p"
              lang={isHebrewUi ? 'he' : 'en'}
              dir={isHebrewUi ? 'rtl' : 'ltr'}
              sx={{
                ...idfErrorDialogTextSx,
                fontSize: '16px',
                whiteSpace: 'nowrap',
              }}
            >
              {t('REVISION_ERROR_PARSER_SUPPORT_FOOTER_LINE')}{' '}
              <Link href={`mailto:${DESIGNBUILDER_SUPPORT_EMAIL}`} underline="always">
                {DESIGNBUILDER_SUPPORT_EMAIL}
              </Link>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RevisionError;
